import { supabase } from '../lib/supabase.js';
import { calcEventBudget } from '../utils/vypocet-mezd.js';
import { openPayoutReceipt } from '../components/payout-receipt-modal.js';

let settingsCache = null;
async function getSettings() {
    if (settingsCache) return settingsCache;
    const { data } = await supabase.from('settings').select('key, value');
    settingsCache = Object.fromEntries((data || []).map(s => [s.key, s.value]));
    return settingsCache;
}

function esc(s) { if (s == null) return ''; return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
function fmtKc(n) { return new Intl.NumberFormat('cs-CZ').format(Math.round(n || 0)) + ' Kč'; }
function fmtDate(d) { const dt = new Date(d); return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`; }

/**
 * Pro každou akci spočítej výplatu každého muzikanta + zjisti, zda už byla vyplacena.
 * Vrací mapu musicianId -> { profile, events: [{event, payout, paid, payoutDate, payoutId}] }
 */
async function loadData() {
    const settings = await getSettings();

    const [{ data: events }, { data: payoutEventsRaw }, { data: profiles }] = await Promise.all([
        supabase.from('events').select(`
            *,
            place:places(name),
            musicians:event_musicians(*, profile:profiles(id, name, musician_role, share_percent))
        `).eq('type', 'gig').order('event_date', { ascending: false }),
        supabase.from('payout_events').select('*, payout:payouts(id, paid_at, musician_id, amount)'),
        supabase.from('profiles').select('*').order('name')
    ]);

    // Map: paidSet has key "eventId|musicianId" -> { date, payoutId }
    const paidMap = new Map();
    (payoutEventsRaw || []).forEach(pe => {
        const muzId = pe.payout?.musician_id;
        if (muzId) paidMap.set(`${pe.event_id}|${muzId}`, { date: pe.payout.paid_at, payoutId: pe.payout.id, amount: pe.amount });
    });

    // For each event, calc budget and split per musician
    const eventsCalc = (events || []).map(ev => {
        const musicians = (ev.musicians || []).map(em => ({
            rowId: em.id, musicianId: em.musician_id,
            name: em.profile?.name || '?',
            role: em.profile?.musician_role,
            km: em.km || 0,
            share: em.share_percent || em.profile?.share_percent || 100
        }));
        const budget = calcEventBudget({
            price: Number(ev.price_total) || 0,
            equipmentRent: Number(ev.equipment_rent) || 0,
            equipmentKm: Number(ev.equipment_km) || 0,
            foodAllowance: Number(ev.food_allowance) || 0,
            vanRate: Number(ev.rate_van_per_km) || Number(settings.rate_van_per_km) || 12,
            carRate: Number(ev.rate_car_per_km) || Number(settings.rate_car_per_km) || 8,
            targetBandWage: Number(ev.target_band_wage) || Number(settings.target_band_wage) || 500,
            roundingStep: Number(ev.rounding_step) || Number(settings.rounding_step) || 100,
            musicians: musicians.map(m => ({ id: m.rowId, share: m.share, km: m.km }))
        });
        return { event: ev, musicians, budget };
    });

    // Map per musicianId -> events list
    const perMusician = new Map();
    (profiles || []).forEach(p => perMusician.set(p.id, { profile: p, events: [], unpaid: 0 }));

    eventsCalc.forEach(({ event, musicians, budget }) => {
        musicians.forEach(m => {
            const wage = budget.wages.find(w => w.id === m.rowId);
            const payout = wage?.payout || 0;
            const key = `${event.id}|${m.musicianId}`;
            const paidInfo = paidMap.get(key);
            const entry = perMusician.get(m.musicianId);
            if (!entry) return;
            entry.events.push({
                event,
                placeName: event.place?.name,
                wage:   wage?.wage   || 0,
                travel: wage?.travel || 0,
                payout,
                paid: !!paidInfo,
                paidDate: paidInfo?.date,
                payoutId: paidInfo?.payoutId
            });
            if (!paidInfo) entry.unpaid += payout;
        });
    });

    // Payouts history per musician
    const { data: payouts } = await supabase.from('payouts').select('*').order('paid_at', { ascending: false });
    const historyByMuz = new Map();
    (payouts || []).forEach(p => {
        if (!historyByMuz.has(p.musician_id)) historyByMuz.set(p.musician_id, []);
        historyByMuz.get(p.musician_id).push(p);
    });

    return { perMusician, historyByMuz };
}

let state = {
    activeMusicianId: null,
    selectedEventIds: new Set()
};

function renderLeft(perMusician) {
    const items = Array.from(perMusician.entries())
        .filter(([, v]) => v.events.length > 0)
        .map(([id, v]) => {
            const isActive = id === state.activeMusicianId;
            const hostTag = v.profile.musician_role === 'stalý host'
                ? ` <span class="host-tag">${v.profile.share_percent}%</span>` : '';
            return `<li class="${isActive ? 'active' : ''}" data-id="${id}">
                <span class="muz-name">${esc(v.profile.name)}${hostTag}</span>
                <span class="muz-meta">nevyplaceno: <span class="owed${v.unpaid === 0 ? ' zero' : ''}">${fmtKc(v.unpaid)}</span></span>
                ${v.unpaid > 0 ? `<button class="pay-now-btn" data-pay-all="${id}">✓ Vyplatit ${fmtKc(v.unpaid)}</button>` : ''}
            </li>`;
        }).join('');

    if (!items) return '<li style="padding:14px;color:var(--text-muted);font-size:13px;">Žádní muzikanti zatím nehráli na žádné akci.</li>';
    return items;
}

function renderMiddle(perMusician) {
    const data = perMusician.get(state.activeMusicianId);
    if (!data) {
        return '<div style="padding:30px;text-align:center;color:var(--text-muted);">Vyber muzikanta vlevo.</div>';
    }
    // Pouze nevyplacené akce — vyplacené najdeš v pravém sloupci "Historie výplat".
    const activeEvents = data.events.filter(e => !e.paid);
    const rows = activeEvents.map(({ event, placeName, wage, travel, payout }) => {
        const cls = state.selectedEventIds.has(event.id) ? 'selected' : '';
        const travelCls = travel > 0 ? 'with-travel' : 'no-travel';
        return `<div class="row ${cls}" data-event="${event.id}">
            <span class="date">${fmtDate(event.event_date)}</span>
            <span class="place">${esc(placeName || '—')}</span>
            <span class="amount-cell">
                <span class="breakdown">
                    <span class="bd-wage" title="Mzda za akci">${fmtKc(wage)}</span>
                    <span class="bd-sep">+</span>
                    <span class="bd-travel ${travelCls}" title="Doprava (osobní auto)">${fmtKc(travel)}</span>
                    <span class="bd-sep">=</span>
                </span>
                <span class="amount">${fmtKc(payout)}</span>
            </span>
            <span class="check"></span>
        </div>`;
    }).join('');

    const selectedSum = activeEvents
        .filter(e => state.selectedEventIds.has(e.event.id))
        .reduce((s, e) => s + e.payout, 0);
    const selectedCount = activeEvents.filter(e => state.selectedEventIds.has(e.event.id)).length;

    const emptyMsg = data.events.length > 0
        ? '<div style="padding:30px;text-align:center;color:var(--text-muted);font-size:13px;">Všechny akce vyplaceny ✓<br><span style="font-size:11px;">Najdeš je v <em>Historie výplat</em> vpravo.</span></div>'
        : '<div style="padding:14px;color:var(--text-muted);font-size:13px;">Žádné akce.</div>';

    return `
        <div class="events-pay-list">${rows || emptyMsg}</div>
        <div class="payout-action-bar">
            <span class="sum">
                <span class="label">Vybráno k vyplacení</span>
                ${fmtKc(selectedSum)} <span style="font-size:12px;color:var(--text-muted);font-weight:400;">(${selectedCount} ${selectedCount === 1 ? 'akce' : 'akcí'})</span>
            </span>
            <button class="btn btn-primary" id="pay-selected-btn" ${selectedCount === 0 ? 'disabled' : ''}>Vyplatit a uložit</button>
        </div>
    `;
}

function renderHistory(historyByMuz) {
    const list = historyByMuz.get(state.activeMusicianId) || [];
    if (!list.length) {
        return '<div style="padding:14px;color:var(--text-muted);font-size:13px;">Žádná výplata zatím.</div>';
    }
    return list.map(p => `
        <div class="pay" data-payout="${p.id}">
            <span class="date">${fmtDate(p.paid_at)}</span>
            <span class="amount">${fmtKc(p.amount)}</span>
            ${p.note ? `<span class="meta">${esc(p.note)}</span>` : ''}
            <button class="pay-share" data-share-payout="${p.id}" title="Vygenerovat potvrzení a sdílet">📤</button>
        </div>
    `).join('');
}

export async function renderMzdy(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';
    const { perMusician, historyByMuz } = await loadData();

    if (!state.activeMusicianId) {
        const firstWithEvents = Array.from(perMusician.entries()).find(([, v]) => v.events.length);
        state.activeMusicianId = firstWithEvents?.[0] || null;
    }

    container.innerHTML = `
        <div class="filters">
            <h2 style="font-size:18px; margin-right:auto;">Mzdy a výplaty</h2>
            <button class="btn btn-sm" disabled>Export CSV</button>
        </div>
        <div class="payouts-layout">
            <div class="payouts-card">
                <div class="card-head">Muzikanti</div>
                <ul class="muz-list-pick" id="muz-list">${renderLeft(perMusician)}</ul>
            </div>
            <div class="payouts-card">
                <div class="card-head">Akce muzikanta (klikni na akci pro vyplacení)</div>
                <div id="middle">${renderMiddle(perMusician)}</div>
            </div>
            <div class="payouts-card">
                <div class="card-head">Historie výplat</div>
                <div class="payout-history" id="history">${renderHistory(historyByMuz)}</div>
            </div>
        </div>
        <div class="footer-note">
            <strong>Workflow:</strong> bílá = nevyplaceno (klikni), žlutá = vybráno.
            Po vyplacení akce zmizí z prostředku a přesune se do <em>Historie výplat</em> vpravo
            (kde ji lze 📤 znovu sdílet).
            Tlačítko <em>✓ Vyplatit X Kč</em> u jména hromadně vyplatí všechny nevyplacené akce muzikanta.
        </div>
    `;

    // Click muzikant
    container.querySelectorAll('#muz-list li').forEach(li => {
        li.addEventListener('click', (e) => {
            if (e.target.dataset.payAll) return;
            state.activeMusicianId = li.dataset.id;
            state.selectedEventIds.clear();
            renderMzdy(container);
        });
    });

    // Pay all button
    container.querySelectorAll('[data-pay-all]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const muzId = btn.dataset.payAll;
            await payAll(muzId, perMusician);
            renderMzdy(container);
        });
    });

    // Click event row (selection)
    container.querySelectorAll('.events-pay-list .row').forEach(row => {
        if (row.classList.contains('paid')) return;
        row.addEventListener('click', () => {
            const id = row.dataset.event;
            if (state.selectedEventIds.has(id)) state.selectedEventIds.delete(id);
            else state.selectedEventIds.add(id);
            // re-render middle only
            document.getElementById('middle').innerHTML = renderMiddle(perMusician);
            wireMiddle(container, perMusician);
        });
    });

    wireMiddle(container, perMusician);

    // Sdílet historickou výplatu — vygeneruje stejný výpis jako po vyplacení.
    // Položky se hledají v už načtených datech přes payoutId.
    container.querySelectorAll('[data-share-payout]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const payoutId = btn.dataset.sharePayout;
            // Aktivní muzikant odpovídá historii — najdeme jeho data
            const musicianId = state.activeMusicianId;
            const data = perMusician.get(musicianId);
            if (!data) return;
            const items = data.events.filter(ev => ev.payoutId === payoutId);
            const payoutRow = (historyByMuz.get(musicianId) || []).find(p => p.id === payoutId);
            if (!payoutRow) return;
            openPayoutReceipt({
                musician: data.profile,
                paidAt: payoutRow.paid_at,
                items
            });
        });
    });
}

function wireMiddle(container, perMusician) {
    container.querySelectorAll('.events-pay-list .row').forEach(row => {
        if (row.classList.contains('paid')) return;
        row.onclick = () => {
            const id = row.dataset.event;
            if (state.selectedEventIds.has(id)) state.selectedEventIds.delete(id);
            else state.selectedEventIds.add(id);
            document.getElementById('middle').innerHTML = renderMiddle(perMusician);
            wireMiddle(container, perMusician);
        };
    });

    const payBtn = container.querySelector('#pay-selected-btn');
    if (payBtn) {
        payBtn.onclick = async () => {
            await paySelected(perMusician);
            state.selectedEventIds.clear();
            renderMzdy(container);
        };
    }
}

async function paySelected(perMusician) {
    const data = perMusician.get(state.activeMusicianId);
    if (!data) return;
    const items = data.events.filter(e => state.selectedEventIds.has(e.event.id) && !e.paid);
    if (!items.length) return;

    const total = items.reduce((s, e) => s + e.payout, 0);
    const { data: payout, error } = await supabase
        .from('payouts')
        .insert({
            musician_id: state.activeMusicianId,
            paid_at: new Date().toISOString().slice(0, 10),
            amount: total,
            note: `${items.length} akcí`
        })
        .select()
        .single();

    if (error) { alert('Chyba: ' + error.message); return; }

    const rows = items.map(e => ({ payout_id: payout.id, event_id: e.event.id, amount: e.payout }));
    const { error: e2 } = await supabase.from('payout_events').insert(rows);
    if (e2) { alert('Chyba: ' + e2.message); return; }

    openPayoutReceipt({ musician: data.profile, paidAt: payout.paid_at, items });
}

async function payAll(musicianId, perMusician) {
    const data = perMusician.get(musicianId);
    if (!data) return;
    const items = data.events.filter(e => !e.paid);
    if (!items.length) return;

    state.activeMusicianId = musicianId;

    const total = items.reduce((s, e) => s + e.payout, 0);
    const { data: payout, error } = await supabase
        .from('payouts')
        .insert({
            musician_id: musicianId,
            paid_at: new Date().toISOString().slice(0, 10),
            amount: total,
            note: `${items.length} akcí · vyplaceno najednou`
        })
        .select()
        .single();

    if (error) { alert('Chyba: ' + error.message); return; }

    const rows = items.map(e => ({ payout_id: payout.id, event_id: e.event.id, amount: e.payout }));
    const { error: e2 } = await supabase.from('payout_events').insert(rows);
    if (e2) { alert('Chyba: ' + e2.message); return; }

    openPayoutReceipt({ musician: data.profile, paidAt: payout.paid_at, items });
}
